import type { RobloxMaterial } from '../types'

export interface PhysicalProperties {
  friction: number
  elasticity: number
  frictionWeight: number
  elasticityWeight: number
}

/** Roblox「Default physical properties of base materials」の既定値。重みは保存するが rapier の Average では使わない。 */
export const MATERIAL_PHYSICAL_PROPERTIES: Record<
  RobloxMaterial,
  PhysicalProperties
> = {
  Plastic: {
    friction: 0.3,
    elasticity: 0.5,
    frictionWeight: 1,
    elasticityWeight: 1,
  },
  SmoothPlastic: {
    friction: 0.2,
    elasticity: 0.5,
    frictionWeight: 1,
    elasticityWeight: 1,
  },
  Neon: {
    friction: 0.3,
    elasticity: 0.2,
    frictionWeight: 1,
    elasticityWeight: 1,
  },
  Wood: {
    friction: 0.48,
    elasticity: 0.2,
    frictionWeight: 1,
    elasticityWeight: 1,
  },
  WoodPlanks: {
    friction: 0.48,
    elasticity: 0.2,
    frictionWeight: 1,
    elasticityWeight: 1,
  },
  Marble: {
    friction: 0.2,
    elasticity: 0.17,
    frictionWeight: 1,
    elasticityWeight: 1,
  },
  Basalt: {
    friction: 0.7,
    elasticity: 0.15,
    frictionWeight: 0.3,
    elasticityWeight: 1,
  },
  Slate: {
    friction: 0.4,
    elasticity: 0.2,
    frictionWeight: 1,
    elasticityWeight: 1,
  },
  CrackedLava: {
    friction: 0.65,
    elasticity: 0.15,
    frictionWeight: 1,
    elasticityWeight: 1,
  },
  Concrete: {
    friction: 0.7,
    elasticity: 0.2,
    frictionWeight: 0.3,
    elasticityWeight: 1,
  },
  Limestone: {
    friction: 0.5,
    elasticity: 0.15,
    frictionWeight: 1,
    elasticityWeight: 1,
  },
  Granite: {
    friction: 0.4,
    elasticity: 0.2,
    frictionWeight: 1,
    elasticityWeight: 1,
  },
  Pavement: {
    friction: 0.5,
    elasticity: 0.17,
    frictionWeight: 0.3,
    elasticityWeight: 1,
  },
  Brick: {
    friction: 0.8,
    elasticity: 0.15,
    frictionWeight: 0.3,
    elasticityWeight: 1,
  },
  Pebble: {
    friction: 0.4,
    elasticity: 0.17,
    frictionWeight: 1,
    elasticityWeight: 1.5,
  },
  Cobblestone: {
    friction: 0.5,
    elasticity: 0.17,
    frictionWeight: 1,
    elasticityWeight: 1,
  },
  Rock: {
    friction: 0.5,
    elasticity: 0.17,
    frictionWeight: 1,
    elasticityWeight: 1,
  },
  Sandstone: {
    friction: 0.5,
    elasticity: 0.15,
    frictionWeight: 5,
    elasticityWeight: 1,
  },
  CorrodedMetal: {
    friction: 0.7,
    elasticity: 0.2,
    frictionWeight: 1,
    elasticityWeight: 1,
  },
  DiamondPlate: {
    friction: 0.35,
    elasticity: 0.25,
    frictionWeight: 1,
    elasticityWeight: 1,
  },
  Foil: {
    friction: 0.4,
    elasticity: 0.25,
    frictionWeight: 1,
    elasticityWeight: 1,
  },
  Metal: {
    friction: 0.4,
    elasticity: 0.25,
    frictionWeight: 1,
    elasticityWeight: 1,
  },
  Grass: {
    friction: 0.4,
    elasticity: 0.1,
    frictionWeight: 1,
    elasticityWeight: 1.5,
  },
  LeafyGrass: {
    friction: 0.4,
    elasticity: 0.1,
    frictionWeight: 2,
    elasticityWeight: 2,
  },
  Sand: {
    friction: 0.5,
    elasticity: 0.05,
    frictionWeight: 5,
    elasticityWeight: 2.5,
  },
  Fabric: {
    friction: 0.35,
    elasticity: 0.05,
    frictionWeight: 1,
    elasticityWeight: 1,
  },
  Snow: {
    friction: 0.3,
    elasticity: 0.03,
    frictionWeight: 3,
    elasticityWeight: 4,
  },
  Mud: {
    friction: 0.3,
    elasticity: 0.07,
    frictionWeight: 3,
    elasticityWeight: 4,
  },
  Ground: {
    friction: 0.45,
    elasticity: 0.1,
    frictionWeight: 1,
    elasticityWeight: 1,
  },
  Asphalt: {
    friction: 0.8,
    elasticity: 0.2,
    frictionWeight: 0.3,
    elasticityWeight: 1,
  },
  Salt: {
    friction: 0.5,
    elasticity: 0.05,
    frictionWeight: 1,
    elasticityWeight: 1,
  },
  Ice: {
    friction: 0.02,
    elasticity: 0.15,
    frictionWeight: 3,
    elasticityWeight: 1,
  },
  Glacier: {
    friction: 0.05,
    elasticity: 0.15,
    frictionWeight: 2,
    elasticityWeight: 1,
  },
  Glass: {
    friction: 0.25,
    elasticity: 0.2,
    frictionWeight: 1,
    elasticityWeight: 1,
  },
  ForceField: {
    friction: 0.25,
    elasticity: 0.2,
    frictionWeight: 1,
    elasticityWeight: 1,
  },
  Air: {
    friction: 0.3,
    elasticity: 0.5,
    frictionWeight: 1,
    elasticityWeight: 1,
  },
  Water: {
    friction: 0.3,
    elasticity: 0.5,
    frictionWeight: 1,
    elasticityWeight: 1,
  },
  Cardboard: {
    friction: 0.5,
    elasticity: 0.05,
    frictionWeight: 1,
    elasticityWeight: 2,
  },
  Carpet: {
    friction: 0.4,
    elasticity: 0.25,
    frictionWeight: 1,
    elasticityWeight: 2,
  },
  CeramicTiles: {
    friction: 0.51,
    elasticity: 0.2,
    frictionWeight: 1,
    elasticityWeight: 1,
  },
  ClayRoofTiles: {
    friction: 0.51,
    elasticity: 0.2,
    frictionWeight: 1,
    elasticityWeight: 1,
  },
  RoofShingles: {
    friction: 0.8,
    elasticity: 0.2,
    frictionWeight: 0.3,
    elasticityWeight: 1,
  },
  Leather: {
    friction: 0.35,
    elasticity: 0.25,
    frictionWeight: 1,
    elasticityWeight: 1,
  },
  Plaster: {
    friction: 0.6,
    elasticity: 0.2,
    frictionWeight: 0.3,
    elasticityWeight: 1,
  },
  Rubber: {
    friction: 1.5,
    elasticity: 0.95,
    frictionWeight: 3,
    elasticityWeight: 2,
  },
}

/** パーツ固有の CustomPhysicalProperties を材質の既定値より優先する。 */
export const physicalPropertiesFor = (
  material: RobloxMaterial,
  custom?: PhysicalProperties,
): PhysicalProperties => custom ?? MATERIAL_PHYSICAL_PROPERTIES[material]
